import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import FeedServices from "@/services/feedServices";
import SuitServices from "@/services/suitServices";
import UnclaimedServices from "@/services/unclaimedServices";
import { useHotelReservations } from "./useHotelReservations";
import { usePurchasedProducts } from "./usePurchasedProducts";
import { useLocalSearchParams } from "expo-router";
import { useFlightReservations } from "./useFlightReservations";
import { useOffers } from "./useOffers";
import { getPlatform } from "@/utils/platform";
import useAppStore from "@/store/app";

export function useHomeScreenData() {
  const params = useLocalSearchParams();
  const { hasSession } = useAppStore();
  // Feed data
  const {
    data: feedResponse,
    isLoading: isFeedLoading,
    refetch: refetchFeed,
  } = useQuery({
    queryKey: ["feedList"],
    queryFn: FeedServices.getList,
  });

  // Suit data
  const { data: suitResponse, refetch: refetchSuit, isLoading: isSuitLoading } = useQuery({
    queryKey: ["suit"],
    queryFn: SuitServices.getSuit,
  });

  // Unclaimed data
  const { data: unclaimedResponse, refetch: refetchUnclaimed } = useQuery({
    queryKey: ["unclaimed"],
    enabled: hasSession,
    queryFn: UnclaimedServices.getUnclaimedSearch,
  });

  // Hotel reservations
  const { reservations, refetch: refetchHotelReservations } =
    useHotelReservations();

  // Purchased products
  const { products, refetch: refetchPurchasedProducts } =
    usePurchasedProducts();

  const { offers: adgemOffers, refetch: refetchAdgem } = useOffers();

  // Flight reservations
  const {
    reservations: flightReservations,
    refetch: refetchFlightReservations,
  } = useFlightReservations();

  const billNegotiatorCardItem = useMemo(() => {
    return {
      id: "internet-discount-fixed",
      type: "billNegotiator",
    };
  }, []);

  const addShortData = (list: any[]) => {
    return list.map((item) => ({
      ...item,
      short: Math.random(),
    }));
  };

  // Derived data
  const listFeed = useMemo(() => {
    const firstId = params.claim;
    let feedList = addShortData([
      billNegotiatorCardItem,
      ...(feedResponse?.data?.data || []),
    ]);
    if (firstId) {
      const item = feedList.find((item) => item.id === firstId);
      if (item) {
        item.short = 1;
      }
    }
    return feedList;
  }, [feedResponse, params.first, billNegotiatorCardItem]);
  const suitList = useMemo(() => {
    return addShortData(suitResponse?.data || []);
  }, [suitResponse]);
  const unclaimedList = useMemo(() => {
    const items = addShortData(unclaimedResponse?.data?.result?.items || []);
    if (params.first == "unclaimedMoney") {
      for (const item of items) {
        item.short = 1;
      }
    }
    return items;
  }, [unclaimedResponse, params.first]);
  const hotelReservationsList = useMemo(() => {
    return addShortData(reservations);
  }, [reservations]);
  const purchasedProductsList = useMemo(() => {
    return addShortData(products);
  }, [products]);
  const flightReservationsList = useMemo(() => {
    return addShortData(flightReservations);
  }, [flightReservations]);
  const adgemOffersList = useMemo(() => {
    return addShortData(adgemOffers);
  }, [adgemOffers]);
  // Refresh all data
  const refreshAllData = useCallback(() => {
    // Public endpoints - always refresh
    refetchFeed();
    refetchSuit();
    refetchAdgem();

    // Authenticated endpoints - only refresh if user has a session
    // refetch() bypasses React Query's enabled check, so we guard here
    if (hasSession) {
      refetchHotelReservations();
      refetchPurchasedProducts();
      refetchFlightReservations();
      refetchUnclaimed();
    }
  }, [
    hasSession,
    refetchFeed,
    refetchSuit,
    refetchHotelReservations,
    refetchPurchasedProducts,
    refetchFlightReservations,
    refetchUnclaimed,
    refetchAdgem,
  ]);

  return {
    // Raw data
    listFeed,
    suitList,
    unclaimedList,
    hotelReservationsList,
    purchasedProductsList,
    flightReservationsList,
    adgemOffersList,
    // Loading states - wait for BOTH feed AND suits to prevent race condition
    isLoading: isFeedLoading || isSuitLoading,

    // Refresh functions
    refreshAllData,
    refetchFeed,
  };
}
